import React, { useContext, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { QueryClient, useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  GeneralActions,
  Icons,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { MdOutlineForwardToInbox } from "react-icons/md";
import { RiUserSettingsLine } from "react-icons/ri";
import { useHistory } from "react-router-dom";

import { AppConfirmationModal, AppConfirmationModalRef } from "Components/AppConfirmationModal";
import { Button } from "../../../../../../../components/Form";
import { Color } from "../../../../../../../components/Form/Button";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { LoadingSpinner } from "../../../../../../../components/LoadingModal/styles";
import { PRIVATE_ROUTES } from "../../../../../../../constants";
import { useAppState } from "../../../../../../../context/StateContext";
import { Applications } from "../../../../../../../models/applications";
import { Branch } from "../../../../../../../models/branch";
import { Member } from "../../../../../../../models/members/member";
import { Filters, Searches, Sort, Sorts } from "../../../../../../../models/shipeezi.table";
import ApplicationsService from "../../../../../../../services/applications.service";
import MembersService from "../../../../../../../services/members.service";
import RoleService from "../../../../../../../services/role.service";
import { CACHE_TIME, STALE_TIME_OF_TWO_MINUTES } from "../../../../../../../util/constants";
import { Status, UserStatus } from "../../../../../../../util/enums";
import { TypeOfMember } from "../../../../../../../util/enums/members";
import getRelatedFilters from "../../../../../../../util/getRelatedFilters";
import { ApplicationKeys, MemberKeys, RoleKeys } from "../../../../../../../util/queries";
import { MemberShowDrawer, MemberShowDrawerHandles } from "./components/MemberShowDrawer";
import { MemberContext } from "./context";
import { getMembersTableColumns } from "./getMembersTableColumns";
import useStyles, { ButtonText } from "./styles";
import CompanyService from "Services/company.service";
import { ModalToInviteMember } from "./ModalToInviteMember";

type Props = {
  permission?: {
    edit: boolean;
    create: boolean;
    resendWelcomeEmail: boolean;
  };
};

export function MemberComponent(props: Props) {
  const { permission } = props;
  const { t: translate } = useTranslation();
  const { currentUser, isMovingVersion } = useAppState();
  const history = useHistory();
  const classes = useStyles();
  const queryClient = new QueryClient();
  const {
    isFetching,
    searches,
    setSearches,
    sorting,
    setSorting,
    filters,
    setFilters,
    page,
    setPage,
    membersList,
    pageSize,
    setPageSize,
    refetchMembers,
  } = useContext(MemberContext);
  const memberShowDrawerRef = useRef<MemberShowDrawerHandles>(null);
  const confirmationModalRef = useRef<AppConfirmationModalRef>(null);
  const [openInviteMember, setOpenInviteMember] = useState<boolean>(false);

  const { mutate: inviteMember, isLoading: isLoadingInviteMember } = useMutation(
    ["company.invite.user"],
    ({
      userEmail,
      roleId,
      typeUser,
      code,
      branches,
      applications,
    }: {
      userEmail: string;
      roleId: number;
      typeUser: string;
      code: string;
      branches: Branch[];
      applications?: Applications[];
    }) =>
      CompanyService.inviteUserInCompany(
        userEmail,
        undefined,
        roleId,
        typeUser,
        undefined,
        code,
        applications,
        branches
      ),
    {
      onSuccess: () => {
        setOpenInviteMember(false);
        NotificationToast.success(translate("inviteSentSuccessfully"));
        refetchMembers();
      },
    }
  );

  const onChangeStatus = (status: string) => {
    if (status === Status.ACTIVE) return Status.INACTIVE;

    return Status.ACTIVE;
  };

  const isPendingAcceptance = (member?: Member) =>
    member?.status?.toLowerCase() === Status.PENDING_ACCEPTANCE.toLowerCase();

  const { data: membersTotal, isFetching: isCounting } = useQuery({
    queryKey: MemberKeys.count([searches, sorting, filters, currentUser?.branches]),
    queryFn: async () => {
      const response = await MembersService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches?.length ? JSON.stringify(searches) : "",
        include: JSON.stringify([
          {
            association: "userCompany",
            include: [
              {
                association: "role",
                attributes: ["id", "name"],
                required: !!getRelatedFilters(filters, "role"),
                where: Object.values(filters ?? {}).length
                  ? getRelatedFilters(filters, "role")
                  : null,
              },
              {
                association: "applications",
                attributes: ["id", "name"],
                required: !!getRelatedFilters(filters, "application"),
                where: Object.values(filters ?? {}).length
                  ? getRelatedFilters(filters, "application")
                  : null,
              },
            ],
            required: !!(
              getRelatedFilters(filters, "role") || getRelatedFilters(filters, "application")
            ),
          },
          {
            association: "location",
            attributes: ["id", "longAddress"],
          },
          {
            association: "branches",
            attributes: ["id", "name", "companyId"],
            where: {
              id: currentUser?.branches?.map((branch) => branch.id),
              companyId: currentUser?.companyId,
            },
            required: false,
          },
        ]),
        ...getRelatedFilters(filters, "member"),
        distinctCount: true,
      });

      if (response?.data) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME_OF_TWO_MINUTES,
    cacheTime: CACHE_TIME,
  });

  const { data: applicationsList } = useQuery({
    queryKey: ApplicationKeys.getAll([]),
    queryFn: async () => {
      const response = await ApplicationsService.getAll({
        include: JSON.stringify([
          {
            association: "profiles",
            where: {
              initials: currentUser?.company?.profile?.initials,
            },
          },
        ]),
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const { data: rolesList } = useQuery({
    queryKey: RoleKeys.getAll([]),
    queryFn: async () => {
      const response = await RoleService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      });

      if (response && response?.data) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const {
    mutate: updateMember,
    variables,
    isLoading,
  } = useMutation({
    mutationKey: MemberKeys.update(),
    mutationFn: async ({ id, status }: { id: number; status: Status }) =>
      MembersService.changeStatus(id, status),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      queryClient.invalidateQueries({ queryKey: [MemberKeys.all] });
      refetchMembers();
    },
  });

  const {
    mutate: resendWelcomeEmail,
    variables: resendWelcomeEmailVariables,
    isLoading: isResendingWelcomeEmail,
  } = useMutation({
    mutationFn: async ({ userId }: { userId: number }) => MembersService.resendWelcomeEmail(userId),
    onSuccess: () => {
      NotificationToast.success(translate("welcomeEmailSentSuccessfully"));
    },
    onError: () => {
      NotificationToast.error(translate("anErrorOccurredTryAgain"));
    },
  });

  const renderInviteMember = () => {
    return (
      <Button
        className={classes.buttonInviteUser}
        tooltip={translate("inviteMember")}
        customColor={Color.SECONDARY_DARK_OUTLINED}
        data-cy="inviteMember"
        onClick={() => setOpenInviteMember(true)}
      >
        {translate("inviteMember")}
      </Button>
    );
  };

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));

    setSorting(sorting);
  };

  const renderChangeStatusButton = (index: number, member: Member) => {
    if (isPendingAcceptance(member)) return null;

    if (isLoading && variables?.id === member.id && index !== undefined) {
      return <LoadingSpinner size={20} />;
    }

    return (
      <Tooltip title={translate("changeStatus")} placement="top">
        <div>
          <Icons.status
            className={classes.buttonIcon}
            onClick={() => {
              if (member) {
                updateMember({
                  id: Number(member?.id),
                  status: onChangeStatus(member.status),
                });
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const renderViewButton = (_index: number, member: Member) => {
    return (
      <Tooltip title={translate("view")} placement="top">
        <div>
          <Icons.view
            className={classes.buttonIcon}
            size={18}
            color="#475467"
            onClick={() => {
              if (!member) return;

              memberShowDrawerRef.current?.open(member);
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const renderEditButton = (_index: number, member: Member) => {
    if (isPendingAcceptance(member)) return null;

    return (
      <Tooltip title={translate("edit")} placement="top">
        <div>
          <Icons.edit
            className={classes.buttonIcon}
            size={18}
            color="#475467"
            onClick={() => {
              const state = history.location.state as any;

              history.push(`${PRIVATE_ROUTES.MEMBERS_MNT}/${member?.id}`, state ?? {});
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const renderResendWelcomeEmailButton = (_index: number, member: Member) => {
    if (isResendingWelcomeEmail && resendWelcomeEmailVariables?.userId === member?.userId) {
      return <LoadingSpinner size={20} />;
    }

    return (
      <Tooltip title={translate("resendWelcomeEmail")} placement="top">
        <div>
          <MdOutlineForwardToInbox
            className={classes.buttonIcon}
            size={18}
            color="#475467"
            onClick={async () => {
              if (!member?.userId) return;

              const confirmed =
                (await confirmationModalRef.current?.open({
                  title: translate("resendWelcomeEmail"),
                  message: translate("resendWelcomeEmailConfirmation").replace(
                    "[name]",
                    member?.fullName || member?.firstName || ""
                  ),
                })) ?? false;

              if (!confirmed) return;

              resendWelcomeEmail({ userId: Number(member.userId) });
            }}
          />
        </div>
      </Tooltip>
    );
  };

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const renderAssignRoleButton = (_userId: number, member: Member) => {
    if (
      member?.userCompany?.status !== UserStatus.INVITATION_SENT &&
      member?.userCompany?.status !== UserStatus.REJECTED &&
      member?.userCompany?.status !== UserStatus.REVOKED &&
      member?.typeOfMember?.toLowerCase() !== TypeOfMember.STAFF?.toLowerCase()
    ) {
      return (
        <Tooltip title={translate("assignRole") || ""} placement="top">
          <div>
            <RiUserSettingsLine
              className={classes.buttonIcon}
              data-cy="action-assign"
              size={18}
              color="#475467"
              onClick={() => {}}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const columns = getMembersTableColumns({ translate });

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleInviteMember", "newRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("typeOfMember"),
          column: "member.typeOfMember",
          options: [TypeOfMember.STAFF, TypeOfMember.USER],
        },
        {
          name: translate("role"),
          column: "role.name",
          options: rolesList?.map((role) => role.name) || [],
        },
        {
          name: translate("accessTo"),
          column: "application.name",
          options: applicationsList?.map((application) => application.name) || [],
        },
        {
          name: translate("status"),
          column: "member.status",
          options: [Status.ACTIVE, Status.INACTIVE, Status.PENDING_ACCEPTANCE],
        },
      ],
      onChange: handleFilter,
    },
    handleInviteMember: permission && !permission.create ? undefined : renderInviteMember(),
    newRecord:
      permission && !permission.create ? undefined : (
        <Button
          customColor={Color.SECONDARY_DARK}
          startIcon={<AddIcon />}
          onClick={() => {
            const state = history.location.state as any;

            history.push(`${PRIVATE_ROUTES.MEMBERS_MNT}`, state ?? {});
          }}
        >
          <ButtonText>{translate("newMember")}</ButtonText>
        </Button>
      ),
  };

  const isUnverifiedMember = (member: Member): boolean =>
    !!member?.userId && !member?.user?.verified && !member?.user?.emailConfirmed;

  const handleRowActionsExtraByPermission = () => {
    let actions: RowActionsExtra[] = [];

    if (permission && permission.edit) {
      actions.push({
        title: translate("edit"),
        key: "edit",
        buttonAndAction: renderEditButton,
      });
    }

    actions?.push(
      {
        title: translate("view"),
        key: "view",
        buttonAndAction: renderViewButton,
      },
      {
        title: translate("changeStatus"),
        key: "changeStatus",
        buttonAndAction: renderChangeStatusButton,
      }
    );

    if (isMovingVersion) {
      actions.push({
        title: translate("resendWelcomeEmail"),
        key: "resendWelcomeEmail",
        buttonAndAction: (index: number, member: Member) =>
          isUnverifiedMember(member) &&
          member?.status?.toLowerCase() === Status.ACTIVE?.toLowerCase()
            ? renderResendWelcomeEmailButton(index, member)
            : null,
      });
    }

    return actions;
  };

  const rowActionsExtra: RowActionsExtra[] = handleRowActionsExtraByPermission();

  return (
    <div>
      <ShipeeziNewGrid
        data={membersList || []}
        columns={columns}
        totalItems={Number(membersTotal || 0)}
        loadingData={isFetching || isCounting}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["edit", "view", "changeStatus", "assign", "resendWelcomeEmail"],
        }}
        rowActionsExtra={rowActionsExtra}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
      <MemberShowDrawer ref={memberShowDrawerRef} />

      {openInviteMember && (
        <ModalToInviteMember
          isOpen={openInviteMember}
          isLoading={isLoadingInviteMember}
          onClose={() => setOpenInviteMember(false)}
          onInviteMember={(email, roleId, typeUser, code, branches, applications) =>
            inviteMember({ userEmail: email, roleId, typeUser, code, branches, applications })
          }
        />
      )}

      <AppConfirmationModal ref={confirmationModalRef} maxWidth="392px" />
    </div>
  );
}
